// /src/components/home/LandingGrid.js
//
// Grid mechanics mirror src/components/projects/ProjectBoard.js exactly so the
// category-filter animation matches the original board:
//   - parent grid has gridAutoRows: ${cellSize}px (measured from the live grid)
//   - each tile motion.div has explicit gridColumn/gridRow span
//   - children fill the fixed-size grid cell (no aspect-square on the child)
// This is what gives the "exit first, then reflow, then enter" feel.

import React, { useEffect, useMemo, useRef, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence, LayoutGroup } from "framer-motion";
import { landingTileIndexBadgeClass } from "../../tokens";

// Split the timing per channel so layout reflow can lead the eye, and
// opacity finishes earlier without dragging a fade through the whole reflow.
//   - layout : how cells slide to their new positions
//   - opacity: enter/exit fade
//   - scale  : intentionally NOT animated — scale composes with layout's
//              transform matrix and is the #1 source of "glitchy" feel.
const TILE_TRANSITION = {
  layout: { duration: 0.5, ease: [0.22, 0.61, 0.36, 1] },
  opacity: { duration: 0.22, ease: "easeOut" },
  default: { duration: 0.4, ease: [0.4, 0, 0.2, 1] },
};

const LandingGrid = ({
  projects = [],
  hoveredIndex,
  onHoverIndex,
  focusedIndex,
  onFocusIndex,
  /** When this grid is a segment of a longer list, global index = local + indexOffset. */
  indexOffset = 0,
  /** Total tiles across all segments (for numbering and ARIA). Defaults to this grid only. */
  totalProjectCount,
}) => {
  const navigate = useNavigate();
  const tileRefs = useRef([]);
  const gridRef = useRef(null);
  const [cols, setCols] = useState(4);
  const [cellSize, setCellSize] = useState(200);

  const sortedProjects = useMemo(
    () => [...projects].sort((a, b) => (a.order || 0) - (b.order || 0)),
    [projects]
  );

  const totalTiles = totalProjectCount ?? sortedProjects.length;

  // Measure the live grid like ProjectBoard, but with a ResizeObserver on the
  // grid element itself so resizes from the layout (rail expanding, sidebar
  // appearing, scrollbar gutter, etc.) update cellSize too — not just window
  // resize. We also read the computed gap so the math doesn't drift when
  // Tailwind's gap class changes at breakpoints.
  useEffect(() => {
    const node = gridRef.current;
    if (!node) return;

    const updateGridInfo = () => {
      const gridStyle = window.getComputedStyle(node);
      const colArray = gridStyle
        .getPropertyValue("grid-template-columns")
        .split(" ")
        .filter(Boolean);
      const colCount = colArray.length || 1;

      // Read the actual column gap from CSS so we don't have to guess by
      // breakpoint. Falls back to 16px if the browser reports `normal`.
      const rawGap =
        gridStyle.getPropertyValue("column-gap") ||
        gridStyle.getPropertyValue("gap");
      const gapValue = parseFloat(rawGap);
      const safeGap = Number.isFinite(gapValue) ? gapValue : 16;

      const containerWidth = node.clientWidth;
      const numberOfGaps = colCount - 1;
      const availableWidth = Math.max(
        containerWidth - numberOfGaps * safeGap,
        0
      );
      const computedCellSize = Math.floor(availableWidth / colCount);

      setCols((prev) => (prev === colCount ? prev : colCount));
      setCellSize((prev) =>
        prev === computedCellSize ? prev : computedCellSize
      );
    };

    updateGridInfo();

    const observer = new ResizeObserver(() => {
      // Defer to next frame so layout has settled before we read.
      requestAnimationFrame(updateGridInfo);
    });
    observer.observe(node);

    return () => observer.disconnect();
  }, []);

  const moveFocus = useCallback(
    (delta) => {
      const maxLocal = sortedProjects.length - 1;
      if (maxLocal < 0) return;

      const inThisGrid =
        focusedIndex != null &&
        focusedIndex >= indexOffset &&
        focusedIndex <= indexOffset + maxLocal;
      const currentLocal = inThisGrid
        ? focusedIndex - indexOffset
        : delta > 0
          ? 0
          : maxLocal;
      let nextLocal = currentLocal + delta;
      if (nextLocal < 0) nextLocal = 0;
      if (nextLocal > maxLocal) nextLocal = maxLocal;
      const global = nextLocal + indexOffset;
      onFocusIndex?.(global);
      onHoverIndex?.(global);
      tileRefs.current[nextLocal]?.focus();
    },
    [
      sortedProjects.length,
      focusedIndex,
      indexOffset,
      onFocusIndex,
      onHoverIndex,
    ]
  );

  const handleKeyDown = useCallback(
    (e, idx) => {
      const project = sortedProjects[idx];
      switch (e.key) {
        case "ArrowRight":
          e.preventDefault();
          moveFocus(1);
          break;
        case "ArrowLeft":
          e.preventDefault();
          moveFocus(-1);
          break;
        case "ArrowDown":
          e.preventDefault();
          moveFocus(cols);
          break;
        case "ArrowUp":
          e.preventDefault();
          moveFocus(-cols);
          break;
        case "Enter":
        case " ":
          if (project?.targetUrl) {
            e.preventDefault();
            navigate(project.targetUrl);
          }
          break;
        default:
          break;
      }
    },
    [sortedProjects, cols, moveFocus, navigate]
  );

  useEffect(() => {
    if (focusedIndex == null) return;
    const local = focusedIndex - indexOffset;
    if (local >= 0 && local < sortedProjects.length && tileRefs.current[local]) {
      tileRefs.current[local]?.focus({ preventScroll: true });
    }
  }, [focusedIndex, indexOffset, sortedProjects.length]);

  return (
    <LayoutGroup>
      <motion.div
        ref={gridRef}
        layout
        role="grid"
        aria-label="Project tiles"
        className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3 sm:gap-4 lg:gap-5 overflow-visible"
        transition={TILE_TRANSITION}
        style={{
          gridAutoFlow: "row",
          gridAutoRows: `${cellSize}px`,
        }}
      >
        <AnimatePresence mode="popLayout">
        {sortedProjects.map((project, idx) => {
          const globalIdx = idx + indexOffset;
          const isActive =
            hoveredIndex === globalIdx ||
            (hoveredIndex == null && focusedIndex === globalIdx);
          const tileNumber = String(globalIdx + 1).padStart(2, "0");
          const hasImage =
            project.mediaSrc &&
            (project.mediaType === "image" ||
              project.mediaType === "gif" ||
              !project.mediaType);

          const colSpan = project.colSpan || 1;
          const rowSpan = project.rowSpan || 1;

          const commonClasses = `
            relative w-full h-full
            overflow-hidden
            bg-[#F5F5F5] dark:bg-[#141414]
            transition-[outline-color] duration-150 ease-out
            outline outline-1
            ${
              isActive
                ? "outline-black dark:outline-white outline-offset-[-1px]"
                : "outline-transparent"
            }
            focus:outline-black focus:dark:outline-white focus:outline-offset-[-1px]
            focus-visible:outline-black focus-visible:dark:outline-white focus-visible:outline-offset-[-1px]
          `;

          return (
            <motion.div
              key={project.title}
              layout
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={TILE_TRANSITION}
              style={{
                gridColumn: `span ${Math.min(colSpan, cols)}`,
                gridRow: `span ${rowSpan}`,
                willChange: "transform, opacity",
                backfaceVisibility: "hidden",
              }}
            >
              <button
                ref={(el) => (tileRefs.current[idx] = el)}
                type="button"
                onMouseEnter={() => onHoverIndex?.(globalIdx)}
                onFocus={() => {
                  onFocusIndex?.(globalIdx);
                  onHoverIndex?.(globalIdx);
                }}
                onBlur={() => onFocusIndex?.(null)}
                onClick={() => project.targetUrl && navigate(project.targetUrl)}
                onKeyDown={(e) => handleKeyDown(e, idx)}
                data-cursor="pointer"
                className={`${commonClasses} clickable cursor-none text-left p-0`}
                aria-label={`${project.title}. Project ${globalIdx + 1} of ${totalTiles}.`}
              >
                {hasImage ? (
                  <>
                    <img
                      src={project.mediaSrc}
                      alt=""
                      loading="lazy"
                      decoding="async"
                      className={`
                        absolute inset-0 m-auto h-full w-full max-h-full max-w-full object-contain object-center
                        transition-[filter,opacity] duration-300 ease-out
                        ${isActive ? "grayscale-0" : "grayscale"}
                        ${isActive ? "opacity-100" : "opacity-[0.92]"}
                        ${project.invert ? "dark:invert" : ""}
                      `}
                    />
                    <span
                      aria-hidden="true"
                      className={`
                        pointer-events-none absolute inset-0
                        bg-neutral-200/35 dark:bg-black/25
                        transition-opacity duration-300 ease-out
                        ${isActive ? "opacity-0" : "opacity-100"}
                      `}
                    />
                  </>
                ) : (
                  <DiagonalHatch />
                )}

                <span
                  className={`${landingTileIndexBadgeClass} ${
                    isActive ? "opacity-100" : "opacity-0"
                  }`}
                  aria-hidden="true"
                >
                  {tileNumber}
                </span>
              </button>
            </motion.div>
          );
        })}
        </AnimatePresence>
      </motion.div>
    </LayoutGroup>
  );
};

const DiagonalHatch = () => (
  <span
    aria-hidden="true"
    className="absolute inset-0 opacity-60 dark:opacity-30"
    style={{
      backgroundImage:
        "repeating-linear-gradient(135deg, rgba(0,0,0,0.06) 0 1px, transparent 1px 8px)",
    }}
  />
);

export default LandingGrid;
